import { createContext, ReactNode, useCallback } from 'react';
import { CompanyDto, UpdateCompanyDto } from 'tdc-web-backend/companies/schemas';
import { AxiosError, AxiosResponse } from 'axios';
import useReferenceOne from '../hooks/crud-hooks/useReferenceOne';
import useAuth from '../hooks/useAuth';
import useUpdate from '../hooks/crud-hooks/useUpdate';

export enum EnumUserCompanyErrorType {
  ServerResponseError = 'ServerResponseError',
  NoCompanyData = 'NoCompanyData',
}

interface IUserCompanyMutateOptions {
  onSuccess?: (data: AxiosResponse) => void;
  onError?: (error: { error: AxiosError | string; type: EnumUserCompanyErrorType }) => void;
}

interface IUserCompanyContextValue {
  company: CompanyDto | null;
  isLoadingUnlockHiring: boolean;
  unlockHiring: (data?: UpdateCompanyDto, options?: IUserCompanyMutateOptions) => void;
  isLoadingUpdateCompany: boolean;
  updateCompany: (data?: UpdateCompanyDto, options?: IUserCompanyMutateOptions) => void;
}

const UserCompanyContext = createContext<IUserCompanyContextValue>({
  company: null,
  isLoadingUnlockHiring: false,
  unlockHiring: () => null,
  isLoadingUpdateCompany: false,
  updateCompany: () => null,
});

export const UserCompanyProvider = ({ children }: { children: ReactNode }) => {
  const {
    authData: { userData },
  } = useAuth();

  const { data: companyData, refetch: refetchCompany } = useReferenceOne<CompanyDto>({
    resource: 'companies',
    id: userData?.membership?.company,
    enabled: !!userData && !!userData.membership?.company,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { mutate: mutateUnlockHiring, isLoading: isLoadingUnlockHiring } = useUpdate({
    resource: 'companies',
  });

  const { mutate: mutateUpdateCompany, isLoading: isLoadingUpdateCompany } = useUpdate({
    resource: 'companies',
  });

  const unlockHiring = useCallback(
    (data?: UpdateCompanyDto, options?: IUserCompanyMutateOptions) => {
      if (!companyData) {
        options?.onError?.({
          error: 'No company data',
          type: EnumUserCompanyErrorType.NoCompanyData,
        });
        return;
      }
      mutateUnlockHiring(
        {
          id: companyData?.id,
          suffix: 'unlock-hiring',
          data: {
            ...data,
          },
        },
        {
          onSuccess: (response) => {
            options?.onSuccess?.(response);
            refetchCompany();
          },
          onError: (error) => {
            options?.onError?.({
              type: EnumUserCompanyErrorType.ServerResponseError,
              error: error as AxiosError,
            });
          },
        },
      );
    },
    [mutateUnlockHiring, companyData],
  );

  const updateCompany = useCallback(
    (data?: UpdateCompanyDto, options?: IUserCompanyMutateOptions) => {
      if (!companyData) {
        options?.onError?.({
          error: 'No company data',
          type: EnumUserCompanyErrorType.NoCompanyData,
        });
        return;
      }
      mutateUpdateCompany(
        {
          id: companyData?.id,
          data: {
            ...data,
          },
        },
        {
          onSuccess: (response) => {
            options?.onSuccess?.(response);
            refetchCompany();
          },
          onError: (error) => {
            options?.onError?.({
              type: EnumUserCompanyErrorType.ServerResponseError,
              error: error as AxiosError,
            });
          },
        },
      );
    },
    [mutateUpdateCompany, companyData],
  );

  const contextValue = {
    company: companyData ?? null,
    isLoadingUnlockHiring,
    unlockHiring,
    updateCompany,
    isLoadingUpdateCompany,
  };

  return <UserCompanyContext.Provider value={contextValue}>{children}</UserCompanyContext.Provider>;
};

export default UserCompanyContext;
